/**
 * <BriefIcon /> — the closed glyph set the Deal Brief can draw.
 *
 * Strokes are deliberately heavy (1.9 on a 24 viewBox): these render ~14px
 * inside a 22px node, where a hairline stroke turns search/clock/check and
 * mail/payment into the same grey smudge on paper.
 *
 * Hand-drawn 24×24 line paths rather than an icon package: the brief prints to
 * PDF and renders on a public share page, so every mark has to be inline vector
 * with no font or network dependency, and has to inherit `currentColor` so the
 * same glyph works on the clay lane, the oxblood lane, and in dark mode.
 *
 * The names are the vocabulary in services/call-brief.ts FLOW_ICONS — the model
 * chooses one per process stage, so the picture is grounded in what the stage
 * is rather than keyword-matched at render time. An unknown name draws the
 * neutral dot instead of throwing.
 */

export type BriefIconName =
  | 'mail'
  | 'chat'
  | 'document'
  | 'folder'
  | 'signature'
  | 'shield'
  | 'search'
  | 'clock'
  | 'people'
  | 'ai'
  | 'workspace'
  | 'payment'
  | 'calendar'
  | 'alert'
  | 'check';

const PATHS: Record<string, JSX.Element> = {
  mail: (
    <>
      <rect x="3" y="5.5" width="18" height="13" rx="2" />
      <path d="M3.5 7l8.5 6 8.5-6" />
    </>
  ),
  chat: (
    <>
      <path d="M20 14.5a2 2 0 0 1-2 2H8l-4 3.5v-14a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2z" />
      <path d="M8.5 9h7M8.5 12h4" />
    </>
  ),
  document: (
    <>
      <path d="M13.5 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8.5z" />
      <path d="M13.5 3v5.5H19M8.5 13h7M8.5 16.5h5" />
    </>
  ),
  folder: (
    <>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </>
  ),
  signature: (
    <>
      <path d="M3 17.5c3 0 3.5-11 6-11s1.5 11 4 11c1.5 0 2-3 3.5-3" />
      <path d="M4 20.5h16" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3l7.5 3v6c0 4.5-3 7.8-7.5 9.5C7.5 19.8 4.5 16.5 4.5 12V6z" />
      <path d="M9 12l2.2 2.2L15.5 10" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M15.8 15.8L20.5 20.5" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5.3l3.4 2" />
    </>
  ),
  people: (
    <>
      <circle cx="9.5" cy="8.5" r="3.2" />
      <path d="M3.8 19.5c0-3.1 2.6-5.2 5.7-5.2s5.7 2.1 5.7 5.2" />
      <path d="M16.2 6.6a3 3 0 0 1 0 5.6M17.5 14.9c1.8.6 3 2.1 3 4.6" />
    </>
  ),
  ai: (
    <>
      <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
      <path d="M18 16l.9 2.1L21 19l-2.1.9L18 22l-.9-2.1L15 19l2.1-.9z" />
    </>
  ),
  workspace: (
    <>
      <rect x="3" y="4.5" width="18" height="15" rx="2" />
      <path d="M3 9h18M7.5 13h5M7.5 16h8" />
    </>
  ),
  payment: (
    <>
      <rect x="3" y="5.5" width="18" height="13" rx="2" />
      <path d="M3 10h18M7 15h3" />
    </>
  ),
  calendar: (
    <>
      <rect x="3.5" y="5" width="17" height="15" rx="2" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
    </>
  ),
  alert: (
    <>
      <path d="M12 4l9 15.5H3z" />
      <path d="M12 10v4M12 16.8v.2" />
    </>
  ),
  check: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8.2 12.2l2.7 2.7 5-5.4" />
    </>
  ),
};

export default function BriefIcon({
  name,
  className,
}: {
  name: BriefIconName | string;
  className?: string;
}) {
  const path = PATHS[name];
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {path ?? <circle cx="12" cy="12" r="3.5" />}
    </svg>
  );
}
